import { useEffect, useRef, useState } from "react";
import { X, ChevronLeft, ChevronRight, MapPin, ExternalLink } from 'lucide-react';
import { sanitizeMarkup } from "../../../util/sanitize/sanitize-html";
import { useAuth } from "../../../providers/auth";
import EventbriteCheckoutModal from "./EventbriteCheckoutModal";

interface ImageLightboxProps {
    isOpen: boolean;
    onClose: () => void;
    data: any;
    initialIndex: number;
    isfromcommunity?: boolean;
}

const ImageLightbox = ({ isOpen, onClose, data, initialIndex, isfromcommunity=false }: ImageLightboxProps) => {
    const [currentIndex, setCurrentIndex] = useState(initialIndex);
    const [showCheckout, setShowCheckout] = useState(false);

    const { users } = useAuth()
    const isEventbriteEvent = data?.source === "eventbrite" && Boolean(data?.eventbriteId);
    useEffect(() => {
        if (isOpen) setCurrentIndex(initialIndex);
    }, [isOpen, initialIndex]);

    useEffect(() => {
        if (!isOpen) {
            setShowCheckout(false);
        }
    }, [isOpen]);

    const handleRegisterClick = () => {
        if (isEventbriteEvent) {
            setShowCheckout(true);
            return;
        }

        window.open(data.link, "_blank");
    };

    if (!isOpen || !data) return null;

    const handleNext = (e: any) => {
        e.stopPropagation();
        setCurrentIndex((prev: any) => (prev + 1) % data.images.length);
    };

    const handlePrev = (e: any) => {
        e.stopPropagation();
        setCurrentIndex((prev: any) => (prev - 1 + data.images.length) % data.images.length);
    };

    return (
        // Backdrop
        <div className="fixed p-12 inset-0 z-50 bg-black/90 flex justify-center items-center">

            {/* Close Button */}
            <button onClick={onClose} className="absolute top-4 right-4 text-white/70 hover:text-white z-50 bg-black/50 rounded-full p-2 transition-colors">
                <X size={24} />
            </button>

            <div className="flex w-full h-full max-w-7xl mx-auto">
                {/* Main Image Viewer Area */}
                <div className="flex-1 relative flex items-center justify-center bg-black/20 h-full">
                    {data.images.length > 1 && (
                        <>
                            <button onClick={handlePrev} className="absolute left-4 text-white/70 hover:text-white bg-black/50 rounded-full p-2 transition-colors">
                                <ChevronLeft size={32} />
                            </button>
                            <button onClick={handleNext} className="absolute right-4 text-white/70 hover:text-white bg-black/50 rounded-full p-2 transition-colors">
                                <ChevronRight size={32} />
                            </button>
                        </>
                    )}
                    <img
                        src={data.images[currentIndex]}
                        alt={`View ${currentIndex + 1}`}
                        className="max-h-full max-w-full object-contain"
                    />
                    {/* Image Counter at bottom */}
                    {data.images.length > 1 && (
                        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-sm bg-black/50 px-3 py-1 rounded-full">
                            {currentIndex + 1} / {data.images.length}
                        </div>
                    )}
                </div>

                {/* Sidebar for Title/Description (Desktop view like image_1.png) */}
                {!isfromcommunity&&<div className="w-[350px] bg-white h-full overflow-y-auto  md:block p-6 shrink-0">
                    <div className="flex items-center mb-4">
                        {/* Placeholder Avatar */}


                    </div>
                    <h2 className="text-xl font-bold  text-instant-teams-blue-Main">{data.title}</h2>
                    {users?.claims.type === "A" && data.author && (
                        <h3 className="font-bold text-instant-teams-blue-D1 mt-1">{data.author}</h3>
                    )}
                    {data?.post_type === "event" && <div className="flex mt-3 justify-center flex-col rounded-2xl p-5 bg-[#F3F4F6] overflow-hidden mb-6 h-auto">
                        {data.location && (
                            <div className="flex items-center text-[#374151] text-sm font-medium">
                                <MapPin size={18} className="mr-3 text-gray-400" strokeWidth={2} />
                                <span>{data.location}</span>
                            </div>
                        )}

                        <button
                            disabled={data?.status === "archived" || data?.pastEvent ? true : false}
                            onClick={handleRegisterClick}
                            className={` w-full  ${data?.status === "archived" || data?.pastEvent ? "bg-gray-300 " : "bg-instant-teams-blue-D1 hover:bg-gray-900 "}  transition-colors text-white  font-semibold py-3.5 px-4 rounded-xl flex items-center justify-center mt-5 text-sm`}>

                            Register
                            {!isEventbriteEvent && <ExternalLink size={18} className="ml-2.5" strokeWidth={2} />}
                        </button>
                    </div>}
                    <div
                        className="prose prose-sm text-[#4B5563] mb-6 max-w-none"
                        dangerouslySetInnerHTML={sanitizeMarkup(data.description)}
                    />


                </div>}
            </div>

            {showCheckout && isEventbriteEvent && (
                <EventbriteCheckoutModal
                    isOpen={showCheckout}
                    onClose={() => setShowCheckout(false)}
                    eventbriteId={data.eventbriteId}
                />
            )}
        </div >
    );
};

export default ImageLightbox;